package com.xtream.webApp

import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.runtime.Composable
import androidx.compose.runtime.CompositionLocalProvider
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.key
import androidx.compose.runtime.remember
import androidx.compose.ui.Modifier
import androidx.compose.ui.focus.FocusDirection
import androidx.compose.ui.input.key.Key
import androidx.compose.ui.input.key.KeyEventType
import androidx.compose.ui.input.key.key
import androidx.compose.ui.input.key.onKeyEvent
import androidx.compose.ui.input.key.type
import androidx.compose.ui.platform.LocalFocusManager
import androidx.compose.ui.platform.LocalLayoutDirection
import androidx.compose.ui.unit.LayoutDirection
import androidx.lifecycle.compose.collectAsStateWithLifecycle
import com.xtream.composeapp.App
import com.xtream.composeapp.AppViewModelStoreOwner
import com.xtream.composeapp.LocalAppViewModelStoreOwner
import com.xtream.composeapp.viewmodel.AppSettingsViewModel
import com.xtream.webApp.presentation.navigation.navEntryProvider
import org.koin.compose.viewmodel.koinViewModel
import kotlin.js.js

@Composable
fun WebApp(modifier: Modifier = Modifier) {
    val rootOwner = remember { AppViewModelStoreOwner() }
    val focusManager = LocalFocusManager.current
    val appSettingsViewModel: AppSettingsViewModel = koinViewModel()

    // 1. Collect the raw language value coming from your view model
    val rawLanguage by appSettingsViewModel.languageFlow.collectAsStateWithLifecycle(null)

    // 2. Map kmr-TR to ku for framework resource and browser compatibility
    val language = remember(rawLanguage) {
        if (rawLanguage?.equals("kmr-TR", ignoreCase = true) == true) "ku" else rawLanguage
    }

    // 3. Compute the Compose layout direction whenever the normalized language alters
    val layoutDirection = remember(language) {
        val lang = language?.lowercase() ?: ""
        if (lang.startsWith("ar") || lang.startsWith("ckb") || lang.startsWith("fa") || lang.startsWith("he") || lang.startsWith("ur")) {
            LayoutDirection.Rtl
        } else {
            LayoutDirection.Ltr
        }
    }

    Box(modifier.fillMaxSize()
        .onKeyEvent { keyEvent ->
            if (keyEvent.type == KeyEventType.KeyDown) {
                when (keyEvent.key) {
                    Key.DirectionUp -> { focusManager.moveFocus(FocusDirection.Up); true }
                    Key.DirectionDown -> { focusManager.moveFocus(FocusDirection.Down); true }
                    Key.DirectionLeft -> { focusManager.moveFocus(FocusDirection.Left); true }
                    Key.DirectionRight -> { focusManager.moveFocus(FocusDirection.Right); true }
                    else -> false
                }
            } else {
                false
            }
        }) {

        // 4. Wrapping with key(language) dumps the old resource cache and updates the UI instantly
        key(language) {
            CompositionLocalProvider(
                LocalAppViewModelStoreOwner provides rootOwner,
                LocalLayoutDirection provides layoutDirection // 👈 Mirrors layout coordinates dynamically
            ) {
                App(
                    entryProvider = navEntryProvider(),
                    appSettingsViewModel = koinViewModel<AppSettingsViewModel>(
                        viewModelStoreOwner = rootOwner
                    )
                )
            }
        }
    }

    // 5. Handles backend storage values and DOM/System attribute synchronization
    LaunchedEffect(language) {
        if (!language.isNullOrBlank()) {
            println("Lang: $language")

            val currentApplied = kotlinx.browser.document.documentElement?.getAttribute("lang")
            if (!language.equals(currentApplied, ignoreCase = true)) {

                // Keep localStorage in sync for application restarts
                kotlinx.browser.localStorage.setItem("app_language", language)
                kotlinx.browser.document.documentElement?.setAttribute("lang", language)

                val lang = language.lowercase()
                val dir = if (lang.startsWith("ar") || lang.startsWith("ckb") || lang.startsWith("fa") || lang.startsWith("he") || lang.startsWith("ur")) {
                    "rtl"
                } else {
                    "ltr"
                }
                kotlinx.browser.document.documentElement?.setAttribute("dir", dir)

                // 6. Wasm & JS Compile-Safe Navigator Mocking
                // Uses a clean static JS script string reading straight from the DOM to avoid runtime interpolation crashes
                runCatching {
                    js("""
                        var targetLang = document.documentElement.getAttribute('lang');
                        Object.defineProperty(window.navigator, 'language', { value: targetLang, configurable: true });
                        Object.defineProperty(window.navigator, 'languages', { value: [targetLang], configurable: true });
                    """)
                }
            }
        }
    }
}